import React from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from './ui/button';
import { getRegion, regionHref } from '../lib/region';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
    
    // Log error for debugging
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // Send to Sentry
    try {
      import('../utils/errorTracking').then(({ captureException }) => {
        captureException(error, {
          component: 'ErrorBoundary',
          action: 'componentDidCatch',
          errorInfo: errorInfo?.componentStack || 'No component stack',
          errorBoundary: true
        });
      }).catch(() => {
        // Silently fail if Sentry not available
      });
    } catch (sentryError) {
      // Silently fail if Sentry not available
      console.warn('Failed to send error to Sentry:', sentryError);
    }
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    // Optionally reload the page for a clean state
    if (this.props.reloadOnRetry) {
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      // This boundary also wraps the region chooser and the US site; the assessment is a Singapore page.
      const inUsRegion = getRegion() === 'us';
      const escapeHref = inUsRegion ? regionHref('us') : regionHref('sg', '/dashboard/eligibility-assessment');
      // The root instance (main.jsx) renders outside `.site-shell`, so the theme sheet never
      // reaches it: paint the sheet tokens inline (with fallbacks) instead of the old gradient.
      return (
        <div
          className="min-h-screen flex items-center justify-center px-4"
          style={{ background: 'var(--sh-paper, #fbfcfe)' }}
        >
          <div
            className="text-center max-w-md mx-auto p-6 bg-white"
            style={{ border: '1px solid var(--sh-rule, #d5deee)', borderRadius: 0 }}
            role="alert"
          >
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4" aria-hidden="true">
              <AlertTriangle className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Something went wrong
            </h2>
            <p className="text-gray-600 mb-6">
              We encountered an error while loading the application. Please try again.
            </p>
            <div className="space-y-3">
              <Button onClick={this.handleRetry} className="w-full">
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
              <Button 
                variant="outline" 
                onClick={() => window.location.href = escapeHref}
                className="w-full"
              >
                {inUsRegion ? 'Return Home' : 'Return to Assessment'}
              </Button>
            </div>
            
            {/* Development error details */}
            {import.meta.env.DEV && this.state.error && (
              <details className="mt-6 text-left">
                <summary className="cursor-pointer text-sm text-gray-500 hover:text-gray-700">
                  Error Details (Development)
                </summary>
                <pre className="mt-2 text-xs text-red-600 bg-red-50 p-3 rounded overflow-auto max-h-32">
                  {this.state.error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
